import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@karma/components';
import { MailIcon } from '@karma/components/icons';
import { skipGuestSignUp } from 'functions/authentication/signUpFunctions';
import Alooma from 'services/alooma';
import * as ALOOMA from 'consts/alooma';
import * as userActions from 'store/user/actions';
import SocialLogin from 'components/molecules/SocialLogin/SocialLogin';
import SignUpWrapperNew from 'components/molecules/SignUpWrapperNew/SignUpWrapperNew';
import SignUpTabs from 'components/molecules/SignUpTabs/SignUpTabs';
import TermsAndPrivacyAgreement from 'components/molecules/TermsAndPrivacyAgreement/TermsAndPrivacyAgreement';
import { AUTHENTICATE_TYPE } from 'consts/authenticateModal';
import { markUrl } from 'functions/markUrl';
import useAction from 'hooks/useAction';
import getIsSeoPage from 'functions/getIsSeoPage';
import { PATHS } from 'consts/paths';
import CConsole from 'functions/CConsole';
import EmailAuthorization from '../EmailAuthorization/EmailAuthorization';
import {
  ButtonsContainer,
  ContentContainer,
  EmailAuthorizationWrapper,
  TabsContainer,
  Title,
} from './SignUpFormNew.style';

const SignUpFormNew = ({ closeModal, authState, setAuthState, isGuestSignup, showImage = true }) => {
  const { t } = useTranslation();
  const contextDataAttrs = Alooma.useContextDataAttrs();
  const fetchUser = useAction(userActions.fetchUser);
  const isSeoPage = getIsSeoPage();
  const [agreementValue, setAgreementValue] = useState(false);
  const [regularLoginSelected, setRegularLoginSelected] = useState(false);
  const [termsError, setTermsError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const isSignUpState = authState === AUTHENTICATE_TYPE.SIGN_UP;

  const openEmailAuthorization = () => {
    if (isSignUpState && !agreementValue) {
      setTermsError(true);

      return;
    }

    setTermsError(false);
    setRegularLoginSelected(true);
  };

  const onChangedTermsCheckbox = () => {
    if (!agreementValue && termsError) setTermsError(false);
    setAgreementValue(v => !v);
  };

  const onSkipSignUp = async () => {
    if (isSignUpState && !agreementValue) {
      setTermsError(true);

      return;
    }

    setTermsError(false);
    setIsFetching(true);

    try {
      await skipGuestSignUp();

      if (isSeoPage) {
        fetchUser();
        closeModal();
      } else {
        window.location.href = PATHS.DASHBOARD;
      }
    } catch (e) {
      CConsole.error(e);
    }

    setIsFetching(false);
  };

  const changeTab = () => {
    if (isSignUpState) {
      markUrl(AUTHENTICATE_TYPE.SIGN_UP, 'false', false);
      markUrl(AUTHENTICATE_TYPE.LOGIN, 'true', true);
      setAuthState(AUTHENTICATE_TYPE.LOGIN);
    } else {
      markUrl(AUTHENTICATE_TYPE.LOGIN, 'false', false);
      markUrl(AUTHENTICATE_TYPE.SIGN_UP, 'true', true);
      setAuthState(AUTHENTICATE_TYPE.SIGN_UP);
    }
  };

  return (
    <SignUpWrapperNew showImage={showImage}>
      {regularLoginSelected ? (
        <EmailAuthorizationWrapper>
          <EmailAuthorization
            isSignUpState={isSignUpState}
            goBack={() => setRegularLoginSelected(false)}
            isGuestSignup={isGuestSignup}
            closeModal={closeModal}
            titleText={isSignUpState ? 'app:joinKarma' : 'app:firstConfirmItsYou'}
            buttonText={isSignUpState ? 'app:letsDoThis' : undefined}
          />
        </EmailAuthorizationWrapper>
      ) : (
        <ContentContainer fullWidth direction="column" justifyContent="space-between">
          <Stack fullWidth direction="column" spacing={32}>
            <TabsContainer fullWidth>
              <SignUpTabs isSignUpState={isSignUpState} changeTab={changeTab} />
            </TabsContainer>
            <Title variant="h3" align="center">
              {isSignUpState ? (
                <>
                  {t('app:signUpAndGet')}
                  <br />
                  {t('app:moneyOnUs', {
                    currency: '$',
                    value: 5,
                  })}
                </>
              ) : (
                t('app:heyYouLetsJumpBackIntoSaving')
              )}
            </Title>
            <ButtonsContainer direction="column" spacing={20}>
              <SocialLogin
                disabled={isFetching}
                agreementValue={isSignUpState ? agreementValue : true}
                setAgreementError={() => setTermsError(true)}
                redirectTo={isSeoPage ? window.location.pathname : null}
              />
              <Button
                Icon={MailIcon}
                fullWidth
                size="small"
                disabled={isFetching}
                onClick={openEmailAuthorization}
                {...contextDataAttrs}
                data-alooma-element={ALOOMA.ELEMENTS.EMAIL}
              >
                {t('app:continueWith', {
                  provider: t('app:email').toLowerCase(),
                })}
              </Button>

              {!!(isSignUpState && isGuestSignup) && (
                <Button
                  type="underlinedText"
                  size="small"
                  onClick={closeModal}
                  disabled={isFetching}
                  {...contextDataAttrs}
                  data-alooma-element={ALOOMA.ELEMENTS.SKIP}
                >
                  {t('app:skipForNow')}
                </Button>
              )}

              {!!(isSignUpState && !isGuestSignup) && (
                <Button
                  type="underlinedText"
                  size="small"
                  onClick={onSkipSignUp}
                  disabled={isFetching}
                  {...contextDataAttrs}
                  data-alooma-element={ALOOMA.ELEMENTS.CONTINUE_AS_GUEST}
                >
                  {t('app:illSignUpLater')}
                </Button>
              )}
            </ButtonsContainer>
          </Stack>
          <Stack direction="column" spacing={5}>
            {isSignUpState && (
              <TermsAndPrivacyAgreement
                error={termsError && t('app:termsAndConditionsError')}
                checked={agreementValue}
                onChangeHandler={onChangedTermsCheckbox}
              />
            )}
          </Stack>
        </ContentContainer>
      )}
    </SignUpWrapperNew>
  );
};

SignUpFormNew.propTypes = {
  authState: PropTypes.oneOf(Object.values(AUTHENTICATE_TYPE)).isRequired,
  setAuthState: PropTypes.func.isRequired,
  isGuestSignup: PropTypes.bool,
  closeModal: PropTypes.func,
  showImage: PropTypes.bool,
};

SignUpFormNew.defaultProps = {
  closeModal: () => null,
  showImage: true,
  isGuestSignup: false,
};

export default SignUpFormNew;
